import React, { useEffect, useRef, useState } from "react";
import ReactTooltip, { TooltipProps } from "react-tooltip-lite";
import styles from "./tooltip.module.css";

interface Props extends TooltipProps {
    content: string;
    children: string | React.ReactNode;
    autoShow?: number;
    autoHide?: number;
    manual?: boolean;
    withCloseButton?: boolean;
    theme?: "dark" | "light";
    onShow?: () => void;
    onHide?: () => void;
    onClose?: () => void;
}

function Tooltip({
    content,
    direction,
    autoShow,
    autoHide,
    arrow,
    children,
    manual = false,
    withCloseButton,
    theme = "dark",
    onShow,
    onHide,
    onClose,
}: Props): JSX.Element {
    const [opened, setOpened] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean | undefined>(undefined);
    const autoShowTimeout = useRef<ReturnType<typeof setTimeout> | null>();
    const autoHideTimeout = useRef<ReturnType<typeof setTimeout> | null>();

    const themeStyles = {
        dark: {
            closeButton: styles.tooltipCloseButtonDark,
            contentInner: styles.tooltipContentInnerDark,
            content: styles.tooltipContentDark,
            contentColor: "#fff",
            contentBackground: "#333",
        },
        light: {
            closeButton: styles.tooltipCloseButtonLight,
            contentInner: styles.tooltipContentInnerLight,
            content: styles.tooltipContentLight,
            contentColor: "#000",
            contentBackground: "#fff",
        },
    };

    function handleHover(): void {
        if (manual) {
            return;
        }

        // Clear timeouts
        if (autoShowTimeout.current) {
            clearTimeout(autoShowTimeout.current);
        }

        if (autoHideTimeout.current) {
            clearTimeout(autoHideTimeout.current);
        }

        // Because of the way React Tooltip Lite is using isOpen prop
        if (isOpen !== undefined) {
            setIsOpen(undefined);
        }

        // Fire onShow or onHide callbacks
        // This version of react-tooltip-lite does not expose prop for callback when the tooltip is shown
        // so we have to track it on our own
        if (!opened) {
            onShow && onShow();
            setOpened(true);
        } else {
            onHide && onHide();
            setOpened(false);
        }
    }

    function handleClose(): void {
        setIsOpen(false);
        setOpened(false);
        onClose && onClose();
        autoHideTimeout.current && clearTimeout(autoHideTimeout.current);
    }

    useEffect(() => {
        if (!!autoShow) {
            autoShowTimeout.current = setTimeout(() => {
                setIsOpen(true);
                setOpened(true);
                onShow && onShow();
            }, autoShow * 1000);
        }

        if (!!autoHide) {
            autoHideTimeout.current = setTimeout(() => {
                setIsOpen(false);
                setOpened(false);
                onHide && onHide();
            }, autoHide * 1000);
        }

        return (): void => {
            // Clear timeouts
            if (autoShowTimeout.current) {
                clearTimeout(autoShowTimeout.current);
            }

            if (autoHideTimeout.current) {
                clearTimeout(autoHideTimeout.current);
            }
        };
    }, [autoShow, autoHide, onShow, onHide]);

    return (
        <ReactTooltip
            content={
                <>
                    {withCloseButton && (
                        // eslint-disable-next-line react/jsx-no-literals
                        <button className={themeStyles[theme].closeButton} onClick={handleClose}>
                            &times;
                        </button>
                    )}

                    <div
                        className={themeStyles[theme].contentInner}
                        dangerouslySetInnerHTML={{ __html: content }}
                    ></div>
                </>
            }
            direction={direction}
            arrow={arrow}
            isOpen={isOpen}
            tipContentClassName={themeStyles[theme].content}
            background={themeStyles[theme].contentBackground}
            color={themeStyles[theme].contentColor}
            padding={withCloseButton ? "0.9rem" : "0.3rem 0.6rem"}
            tagName="span"
            useHover={!manual}
        >
            {typeof children !== "string" ? (
                <span
                    data-testid="inner-span"
                    onMouseEnter={handleHover}
                    onMouseLeave={handleHover}
                >
                    {children}
                </span>
            ) : (
                <span
                    data-testid="inner-span"
                    onMouseEnter={handleHover}
                    onMouseLeave={handleHover}
                    dangerouslySetInnerHTML={{ __html: children }}
                />
            )}
        </ReactTooltip>
    );
}

export default Tooltip;
